<template>
  <div class="payment-page" v-if="plan && plans">
    <modal
      v-if="showPlans && showPlansInModal"
      @dismiss="$store.commit('setShowPlans', false)"
    >
      <plans></plans>
    </modal>

    <template v-if="showPlans && !showPlansInModal">
      <plans
        v-if="plans.length !== 1 || plans[0].features.length > 0"
        @dismiss="$store.commit('setShowPlans', false)"
      >
      </plans>
      <div v-else>
        <div v-if="currentSubscription.interval === 'month'">
          <p>
            Switch to annual plan at ${{ plans[0].annual_price / 100 }} per
            learner per year.
          </p>
          <action-button
            @click.prevent="switchPeriod('annual')"
            class="primary-bg secondary-bg-hover"
            >Switch to Annual</action-button
          >
        </div>
        <div v-if="currentSubscription.interval === 'year'">
          <p>
            Switch to monthly plan at ${{ plans[0].monthly_price / 100 }} per
            learner per month.
          </p>
          <action-button
            @click.prevent="switchPeriod('monthly')"
            class="primary-bg secondary-bg-hover"
            >Switch to Monthly</action-button
          >
        </div>
      </div>
    </template>

    <div v-if="!showPlans || showPlansInModal">
      <div class="subscription-selector-wrapper">
        <subscription-selector
          :showAnnualNudge="showAnnualNudge"
        ></subscription-selector>
        <a
          v-if="
            plan &&
              !plan['hidden_from_selection'] &&
              !noChoice &&
              (!showPlans || showPlansInModal)
          "
          class="change-plan"
          @click.prevent="$store.commit('setShowPlans', true)"
          href=""
          >{{ $t("message.change-plan-or-billing") }}</a
        >
      </div>
      <template v-if="pricingPeriod === 'monthly' && showLearners">
        <h5>
          {{ capitalize($t("message.learners")) }}
        </h5>
        <learners
          @change="
            (learners) => {
              $store.commit('setLearners', learners);
            }
          "
          :initialLearners="learners"
          :min="plan.min_users"
          :max="maxUsers"
          :max-message="plan.max_users_message"
        />
      </template>

      <licence-count
        v-if="pricingPeriod === 'annual' && showLearners"
        :value="licenceCount"
        @change="
          (count) => {
            $store.commit('setLicenceCount', count);
          }
        "
        :unitPrice="unitPrice"
        :min="plan.min_users"
        :max="maxUsers"
        :max-message="plan.max_users_message"
      />

      <plan-summary :allowEdit="false"></plan-summary>

      <discount-code
        v-if="!hideDiscount"
        :showInput="!currentSubscription"
      ></discount-code>

      <div class="disclaimer" v-if="disclaimer" v-html="disclaimer"></div>

      <div class="buttons" v-if="!hideNext">
        <a
          class="lp-btn lp-btn-primary next-btn"
          href=""
          @click.prevent="submitPlan"
          >{{ $t("message.next") }}</a
        >
      </div>
    </div>
  </div>
  <div class="spin-container" v-else>
    <spinner></spinner>
  </div>
</template>

<style lang="less" scoped>
.payment-page {
  position: relative;
  padding-top: 1em;
  p {
    padding-bottom: 1.3em;
  }
  h2 {
    margin-bottom: 1em;
    font-weight: bold;
  }
}
i {
  font-style: italic;
  margin-top: 1em;
}
.change-plan {
  color: rgba(0, 0, 0, 0.5);
  text-decoration: underline;
  margin-top: 0.3em;
  display: block;
  &:hover {
    color: rgba(0, 0, 0, 1);
  }
}

.back-link {
  font-weight: bold;
  display: block;
  color: black;
  font-size: 1.1rem;
  margin-bottom: 0em;
  svg {
    fill: @primary;
  }
}
.proviso,
.disclaimer {
  /deep/ super {
    font-size: 0.6em;
    top: -0.7em;
    position: relative;
  }
  /deep/ strong {
    font-weight: bold;
  }
}

.disclaimer {
  font-size: 0.9em;
}

.subscription-selector-wrapper {
  margin-bottom: 2.5em;
}

.next-btn {
  min-width: 88px;
}
b {
  font-weight: bold;
}
.order-summary {
  margin: 1em;
  overflow: auto;
  dt {
    width: 80px;
    text-align: right;
    color: #999;
    text-transform: uppercase;
    font-size: 0.9em;
    font-weight: bold;
    float: left;
    clear: left;
    position: relative;
    top: 2px;
  }
  dd {
    float: left;
    margin-left: 0.5em;
  }
}

.discount-strikethrough {
  text-decoration: line-through;
}
.toggle-container {
  border-top: 1px solid #ccc;
  padding-top: 1em;
  margin: 1em 0;
}
.spin-container {
  text-align: center;
  /deep/ .spin-element {
    border-color: #e8e8e8 transparent transparent transparent;
  }
}
</style>

<script>
import axios from "axios";
import swal from "sweetalert";
import { mapState, mapGetters } from "vuex";

import ActionButton from "@/common/components/ActionButton";
import Learners from "./Learners";
import SubscriptionSelector from "./SubscriptionSelector";
import PlanSummary from "./PlanSummary";
import Plans from "./Plans";
import LicenceCount from "./LicenceCount";
import DiscountCode from "./DiscountCode";
import Modal from "@/common/components/Modal";
import Toggle from "@/common/components/Toggle";

export default {
  computed: {
    disclaimer() {
      if (this.pricingPeriod === "annual") {
        return this.plan.annual_disclaimer;
      } else {
        return this.plan.monthly_disclaimer;
      }
    },
    planChangeText() {
      if (this.updateNow) {
        return this.capitalize(this.$t("message.immediately"));
      } else if (this.currentSubscription && this.currentSubscription.billing) {
        return this.$moment(this.currentSubscription.billing.next_payment_due).format(
          "MMM Do, Y"
        );
      } else {
        return this.capitalize(this.$t("message.start-of-next-billing"));
      }
    },
    ...mapState({
      plan: (state) => state.payment.plan,
      plans: (state) => state.payment.plans,
      learners: (state) => state.payment.learners,
      annualLearners: (state) => state.payment.annualLearners,
      discount: (state) => state.payment.discount,
      licenceCount: (state) => state.payment.licenceCount,
      pricingPeriod: (state) => state.payment.pricingPeriod,
      showPlans: (state) => state.payment.showPlans,
      currentSubscription: (state) => state.payment.currentSubscription,
    }),
    ...mapGetters([
      "proviso",
      "noChoice",
      "discountActive",
      "maxUsers",
      "unitPrice",
      "currentUser",
      "updateNow",
    ]),
  },
  components: {
    ActionButton,
    Learners,
    Plans,
    LicenceCount,
    DiscountCode,
    SubscriptionSelector,
    PlanSummary,
    Modal,
    Toggle,
  },
  props: {
    subscriptionId: {
      required: false,
    },
    showLearners: {
      required: false,
      default: true,
    },
    showPlansInModal: {
      required: false,
      default: true,
    },
    showAnnualNudge: {
      required: false,
      default: true,
    },
    hideDiscount: {
      required: false,
      default: false,
      type: Boolean,
    },
    hideNext: {
      required: false,
      default: false,
      type: Boolean,
    },
  },
  mounted() {
    if (!this.plans) {
      this.getPlans();
    }
  },
  methods: {
    switchPeriod(period) {
      this.$store.commit("setPlan", this.plans[0]);
      this.$store.commit("setPricingPeriod", period);
      this.$store.commit("setShowPlans", false);
    },
    submitPlan() {
      this.$emit("savePlan");
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    getPlans() {
      axios.get(`/subscriptions/plans`).then((response) => {
        this.$store.commit("setPlans", response.data);
        if (!this.plan) {
          let plan = this.currentSubscription
            ? this.plans.find((p) => p.id == this.currentSubscription.plan.id)
            : response.data[0];
          this.$store.commit("setPlan", plan);
        }
      });
    },
  },
};
</script>
